import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { Granularity } from '@/api/generated/model';
import { computeSpendPerTask } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { alignSeriesToBuckets } from '@/domains/pages/shared/chart-axis';
import { formatTrendBucketLabel, type TrendQueryConfig } from '@/domains/pages/shared/trend-query';
import dayjs from 'dayjs';

export type SpendTrendSeries = {
  labels: string[];
  totalSpend: Array<number | null>;
  spendPerTask: Array<number | null>;
};

function granularityUnit(granularity: Granularity): 'day' | 'week' | 'month' | 'year' {
  switch (granularity) {
    case Granularity.day:
      return 'day';
    case Granularity.week:
      return 'week';
    case Granularity.month:
      return 'month';
    case Granularity.year:
      return 'year';
    default:
      return 'day';
  }
}

/** Normalize API `group_name` and canonical bucket keys to a comparable period start. */
export function normalizeTrendBucketKey(raw: string, granularity: Granularity): string {
  const date = dayjs(raw);
  if (!date.isValid()) return raw;
  const unit = granularityUnit(granularity);
  return date.startOf(unit).format('YYYY-MM-DDTHH:mm:ss');
}

/** Canonical period starts for the trend window, matching the API's `group_name` format. */
export function buildTrendBuckets(trendQuery: TrendQueryConfig): string[] {
  const unit = granularityUnit(trendQuery.granularity);
  const start = dayjs(trendQuery.time_range.start).startOf(unit);
  const end = dayjs(trendQuery.time_range.end).startOf(unit);
  const buckets: string[] = [];

  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    buckets.push(normalizeTrendBucketKey(current.format(), trendQuery.granularity));
    current = current.add(1, unit);
  }

  return buckets;
}

/** Align out-of-order, gap-containing rows onto the canonical buckets. */
export function mapSpendTrendSeries(
  data: PaginatedExtendedGroupByQuery,
  trendQuery: TrendQueryConfig,
): SpendTrendSeries {
  const buckets = buildTrendBuckets(trendQuery);
  const sourceBuckets: string[] = [];
  const totalSpend: Array<number | null> = [];
  const spendPerTask: Array<number | null> = [];

  for (const row of data.data ?? []) {
    const key = normalizeTrendBucketKey(row.group_name ?? '', trendQuery.granularity);
    sourceBuckets.push(key);
    const cost = row.cost ?? null;
    const tasks = row.n_segments ?? 0;
    totalSpend.push(cost);
    spendPerTask.push(computeSpendPerTask(cost, tasks));
  }

  const labels = buckets.map((bucket, index) =>
    formatTrendBucketLabel(bucket, trendQuery.granularity, trendQuery.bucketKind, index === buckets.length - 1),
  );

  return {
    labels,
    totalSpend: alignSeriesToBuckets(buckets, sourceBuckets, totalSpend),
    spendPerTask: alignSeriesToBuckets(buckets, sourceBuckets, spendPerTask),
  };
}
